// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { personSchema } from '@/account/api/types';
import { transportReferenceDetailsSchema } from '@/constants/shipping-carrier';
import { returnOrderReasonSchema } from '@/return-order/api/types';
import {
  sparseItemSchema,
  sparseLocationWithParentSchema,
} from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Status values that can appear on an exchange order entity.
 */
export type ExchangeOrderEntityStatus =
  | 'draft'
  | 'in_progress'
  | 'complete'
  | 'cancelled';

/**
 * Status values for filtering (includes 'all' for filter dropdowns).
 */
export type ExchangeOrderStatus = ExchangeOrderEntityStatus | 'all';

/**
 * Single source of truth for status mapping between frontend and backend.
 */
const STATUS_MAP: Record<ExchangeOrderStatus, string> = {
  draft: 'DRAFT',
  in_progress: 'IN_PROGRESS',
  complete: 'COMPLETE',
  cancelled: 'CANCELLED',
  all: 'ALL',
};

/**
 * Inverse mapping generated from STATUS_MAP.
 */
const INVERSE_STATUS_MAP = Object.fromEntries(
  Object.entries(STATUS_MAP).map(([k, v]) => [v, k])
) as Record<string, ExchangeOrderEntityStatus>;

/**
 * Converts frontend status to API format (uppercase).
 */
export function statusToAPI(status: ExchangeOrderStatus): string {
  return STATUS_MAP[status];
}

/**
 * Converts API status (uppercase) to frontend format (lowercase).
 * Returns 'draft' as fallback for unknown statuses.
 */
function statusFromAPI(status: string): ExchangeOrderEntityStatus {
  return INVERSE_STATUS_MAP[status] ?? 'draft';
}

/**
 * Schema for quantities in an exchange order item.
 * Only requestedQuantity is stored on the EO item - other quantities are computed from linked RO/FO.
 */
const quantitiesSchema = z.object({
  requestedQuantity: z.number(),
  receivedQuantity: z.number().default(0),
  pendingQuantity: z.number().nullable(),
  shippedQuantity: z.number().default(0),
  deliveredQuantity: z.number().default(0),
});

/**
 * Schema for an exchange order item with bidirectional quantities.
 * Items are derived from linked RO/FO and don't have their own id/key.
 * Uses nested item structure for catalog item properties.
 */
const exchangeOrderItemSchema = z.object({
  // Nested catalog item properties
  item: sparseItemSchema,
  returnQuantity: quantitiesSchema,
  fulfillmentQuantity: quantitiesSchema,
});

/**
 * Schema for a linked return order (sparse).
 * Contains returnType and returnReason since these are stored on the RO, not the EO.
 * Field names match the full ReturnOrder response for consistency.
 */
const linkedReturnOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  returnOrderNumber: z.string(),
  returnType: z.string(),
  returnReason: z.string().nullable(),
  fromLocation: sparseLocationWithParentSchema.nullable(),
  toLocation: sparseLocationWithParentSchema,
  promiseShipDate: dateSchema.nullable(),
  promiseDeliveryDate: dateSchema.nullable(),
  status: z.string(),
});

/**
 * Schema for a linked fulfillment order (sparse).
 * Field names match the full FulfillmentOrder response for consistency.
 */
const linkedFulfillmentSchema = z.object({
  id: z.string(),
  key: z.string(),
  fulfillmentOrderNumber: z.string(),
  status: z.string(),
  fromLocation: sparseLocationWithParentSchema,
  toLocation: sparseLocationWithParentSchema,
});

/**
 * Schema for a location pair from a linked RO or FO.
 */
const locationPairSchema = z.object({
  fromLocation: sparseLocationWithParentSchema,
  toLocation: sparseLocationWithParentSchema,
});

/**
 * Schema for an exchange order.
 * Note: type/reason and promise dates are now on the linked RO, not the EO itself.
 * Use returnOrders[0].returnType to get the exchange type.
 * Locations are derived from linked FO/RO as pairs to maintain the logical connection between from/to.
 */
export const exchangeOrderSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    organizationId: z.string(),
    exchangeNumber: z.string(),
    locationPairs: z
      .array(locationPairSchema)
      .nullable()
      .transform((v) => v ?? []),
    status: z.string(),
    orderNotes: z.string().nullable(),
    items: z
      .array(exchangeOrderItemSchema)
      .nullable()
      .transform((v) => v ?? []),
    returnOrders: z
      .array(linkedReturnOrderSchema)
      .nullable()
      .transform((v) => v ?? []),
    fulfillmentOrders: z
      .array(linkedFulfillmentSchema)
      .nullable()
      .transform((v) => v ?? []),
    createdAt: dateTimeSchema,
    createdBy: personSchema,
    lastUpdatedAt: dateTimeSchema,
    lastUpdatedBy: personSchema,
  })
  .transform((data) => ({
    ...data,
    status: statusFromAPI(data.status),
  }));

export type ExchangeOrder = z.infer<typeof exchangeOrderSchema>;

/**
 * Filter parameters for listing exchange orders.
 * Note: type filter removed since type is now on linked RO, not EO.
 */
export interface ExchangeOrderFilter {
  status?: ExchangeOrderStatus;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Schema for creating an exchange order.
 * Wraps the existing return order and fulfillment order create request schemas.
 * This allows the same validation and structure to be reused.
 */
export const createExchangeOrderRequestSchema = z.object({
  // Return order request (reuses existing RO create schema structure)
  returnOrder: z.object({
    returnType: returnOrderReasonSchema,
    returnReason: z.string().nullable(),
    fromLocationId: z.string().nullable(),
    toLocationId: z.string(),
    promiseShipDate: dateSchema.nullable(),
    promiseDeliveryDate: dateSchema.nullable(),
    orderNotes: z.string().nullable(),
    transportReferenceDetails: transportReferenceDetailsSchema.nullable(),
    items: z.array(
      z.object({
        itemId: z.string(),
        requestedQuantity: z.number().min(0),
      })
    ),
    fileIds: z.array(z.string()).nullable(),
  }),

  // Fulfillment order request (reuses existing FO create schema structure)
  fulfillmentOrder: z.object({
    projectId: z.string().nullable(),
    fromLocationId: z.string(),
    toLocationId: z.string(),
    promiseShipDate: dateSchema.nullable(),
    promiseDeliveryDate: dateSchema.nullable(),
    orderNotes: z.string().nullable(),
    contactId: z.string().nullable(),
    items: z.array(
      z.object({
        itemId: z.string(),
        requestedQuantity: z.number().min(0),
      })
    ),
    fileIds: z.array(z.string()).nullable(),
  }),
});

export type CreateExchangeOrderRequest = z.infer<
  typeof createExchangeOrderRequestSchema
>;

export type DetailedExchangeOrder = ExchangeOrder;
